import { useState, useEffect, SyntheticEvent } from "react";
import Button from "@mui/material/Button";
import axios from "axios";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DateField, type DataTableColumn } from "@routes-plan/ui";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import ClientPortfolioModal from "./modals/ClientPortfolioModal";
import DefaultClientsModal from "./modals/DefaultClientsModal";
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { companyLocale, formatCurrency as companyMoney } from '../../utils/format/currency';
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportTable, RouteMultiSelect } from "./components/ReportLayout";

interface BalanceRow {
  id: string;
  route: string;
  routeId: number | string;
  date: string;
  day: string;
  balance: number;
  portfolio: number;
  defaultClientCount: number;
  defaultValue: number;
  cleanBalance: number;
  cashBox: number;
}

// Interfaces
interface Office { id: number; name: string; }
interface Route { id: number; name: string; }

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;          // string para que no se limpie el Select
  selectedRouteIds: number[];
}

// Mismas funciones de aviso y carga que pasa App.
interface ResumenSaldoProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Utils
const formatCurrency = (value: number): string =>
  companyMoney(value);

const formatInt = (value: number): string =>
  new Intl.NumberFormat(companyLocale(), { maximumFractionDigits: 0 }).format(value);

// Componente
const Balances: React.FC<ResumenSaldoProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  const t = useT();
  setPageTitle("menu.reports.balances");
  const currentDate = nowOperationalDateString();

  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(false);


  // Estado de las ventanas de cartera y de clientes en mora
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRouteId, setModalRouteId] = useState<number | null>(null);
  const [modalRouteName, setModalRouteName] = useState<string | null>(null);
  const [modalDate, setModalDate] = useState<string | null>(null);
  const [defaultModalOpen, setDefaultModalOpen] = useState(false);
  const [defaultModalRouteId, setDefaultModalRouteId] = useState<number | null>(null);
  const [defaultModalRouteName, setDefaultModalRouteName] = useState<string | null>(null);
  const [defaultModalDate, setDefaultModalDate] = useState<string | null>(null);

  const openCartulina = (routeId: number, routeName: string, date: string) => {
    setModalRouteId(routeId);
    setModalRouteName(routeName);
    setModalDate(date);
    setModalOpen(true);
  };

  const openDefaultClients = (routeId: number, routeName: string, date: string) => {
    setDefaultModalRouteId(routeId);
    setDefaultModalRouteName(routeName);
    setDefaultModalDate(date);
    setDefaultModalOpen(true);
  };


  // filtro
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedRouteIds: [],
  });

  // datos UI
  const [rows, setRows] = useState<BalanceRow[]>([]);
  const [queried, setQueried] = useState(false);
  const [totals, setTotals] = useState({ balance: 0, cleanBalance: 0, cashBox: 0 });
  // Oficinas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert(t("clientReports.officesLoadError"), "error");
      }
    })();
  }, [showAlert, t]);

  // Cobros por oficina
  useEffect(() => {
    (async () => {
      if (!filter.officeId) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`/offices/${filter.officeId}/routes`);
        setOfficeRoutes(data || []);
        setFilter((prev) => ({ ...prev, selectedRouteIds: [] }));
      } catch (e) {
        console.error(e);
        showAlert(t("clientReports.routesLoadError"), "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [filter.officeId, showAlert, t]);

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert(t("clientReports.selectOfficeWarning"), "warning");
      return false;
    }
    if (filter.selectedRouteIds.length === 0) {
      showAlert(t("clientReports.selectAtLeastOneRoute"), "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(t("clientReports.startAfterEnd"), "warning");
      return false;
    }
    return true;
  };

  const columns: DataTableColumn<BalanceRow>[] = [
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.route, sortable: true },
    { id: 'date', header: t('common.date'), value: (r) => r.date, sortable: true },
    { id: 'day', header: t('clientReports.day'), value: (r) => r.day, hideBelow: 'sm' },
    { id: 'balance', header: t('clientReports.balance'), value: (r) => r.balance, render: (r) => formatCurrency(r.balance), align: 'right', sortable: true },
    {
      id: 'portfolio',
      header: t('clientReports.portfolio'),
      value: (r) => r.portfolio,
      align: 'right',
      sortable: true,
      render: (r) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openCartulina(Number(r.routeId ?? 0), String(r.route ?? ''), String(r.date ?? ''));
          }}
          endIcon={<InfoOutlinedIcon fontSize="small" />}
          aria-label={t('clientReports.portfolio')}
        >
          {formatInt(r.portfolio)}
        </Button>
      ),
    },
    {
      id: 'defaultClientCount',
      header: t('clientReports.defaultClients'),
      value: (r) => r.defaultClientCount,
      align: 'right',
      sortable: true,
      render: (r) =>
        r.defaultClientCount <= 0 ? (
          formatInt(r.defaultClientCount)
        ) : (
          <Button
            size="small"
            color="warning"
            onClick={(e) => {
              e.stopPropagation();
              openDefaultClients(Number(r.routeId ?? 0), String(r.route ?? ''), String(r.date ?? ''));
            }}
            endIcon={<InfoOutlinedIcon fontSize="small" />}
            aria-label={t('clientReports.defaultClients')}
          >
            {formatInt(r.defaultClientCount)}
          </Button>
        ),
    },
    { id: 'defaultValue', header: t('clientReports.defaultValue'), value: (r) => r.defaultValue, render: (r) => formatCurrency(r.defaultValue), align: 'right', sortable: true },
    { id: 'cleanBalance', header: t('clientReports.balanceWithoutDefault'), value: (r) => r.cleanBalance, render: (r) => formatCurrency(r.cleanBalance), align: 'right', sortable: true },
    { id: 'cashBox', header: t('clientReports.cashBox'), value: (r) => r.cashBox, render: (r) => formatCurrency(r.cashBox), align: 'right', sortable: true },
  ];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    setQueried(true);
    try {
      const payload = {
        startDate: filter.startDate,        // "YYYY-MM-DD"
        endDate: filter.endDate,            // "YYYY-MM-DD"
        routeId: filter.selectedRouteIds, // [ids]
      };

      // Saldos por ruta y día en el periodo
      const { data } = await axios.post("/reports/balances", payload);

      const items = Array.isArray(data?.data) ? data.data : [];

      if (items.length === 0) {
        setRows([]);
        setTotals({ balance: 0, cleanBalance: 0, cashBox: 0 });
        showAlert(t("clientReports.noRecordsInPeriod"), "info");
        return;
      }

      const mapped: BalanceRow[] = items.map((r: any, i: number) => ({
        id: `${r.route}-${r.date}-${i}`,
        route: r.route ?? "",
        routeId: r.routeId ?? "",
        date: r.date ?? "",
        day: r.day ?? "",
        balance: Number(r.balance || 0),
        portfolio: Number(r.portfolio || 0),
        defaultClientCount: Number(r.defaultClientCount || 0),
        defaultValue: Number(r.defaultValue || 0),
        cleanBalance: Number(r.cleanBalance || 0),
        cashBox: Number(r.cashBox || 0),
      }));

      const sumBalance = mapped.reduce((acc, it) => acc + it.balance, 0);
      const sumClean = mapped.reduce((acc, it) => acc + it.cleanBalance, 0);
      const sumBox = mapped.reduce((acc, it) => acc + it.cashBox, 0);

      setRows(mapped);
      setTotals({ balance: sumBalance, cleanBalance: sumClean, cashBox: sumBox });

      showAlert(t("clientReports.recordsFound", { count: mapped.length }), "success");
    } catch (error) {
      console.error("Error obteniendo datos:", error);
      showAlert(t("clientReports.dataLoadError"), "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <ReportPage title={t("clientReports.balancesTitle")} description={t("clientReports.balancesDescription")}>
      <ReportFilters onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)} submitDisabled={loading}>
        <DateField
          label={t("clientReports.startDate")}
          value={filter.startDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, startDate: v ?? "" }))}
          max={minYmd(filter.endDate, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("clientReports.endDate")}
          value={filter.endDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, endDate: v ?? "" }))}
          min={filter.startDate}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={filter.officeId} onChange={(id) => setFilter((prev) => ({ ...prev, officeId: id }))} />
        <RouteMultiSelect
          routes={officeRoutes}
          value={filter.selectedRouteIds.map(String)}
          onChange={(ids) => setFilter((prev) => ({ ...prev, selectedRouteIds: ids.map(Number) }))}
        />
      </ReportFilters>

      <ReportResults>
        <ReportTable<BalanceRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          exportFileName={t("clientReports.balancesFileName")}
          ariaLabel={t("clientReports.balancesTitle")}
          emptyState={<ReportEmpty queried={queried} />}
        />
      </ReportResults>

      <ClientPortfolioModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        routeId={modalRouteId}
        date={modalDate}
        routeName={modalRouteName}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <DefaultClientsModal
        open={defaultModalOpen}
        onClose={() => setDefaultModalOpen(false)}
        routeId={defaultModalRouteId}
        routeName={defaultModalRouteName}
        date={defaultModalDate}
        showAlert={showAlert}
      />
    </ReportPage>
  );
};

export default Balances;
